import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import Button from "../Buttons/Button";
import { cardStyles } from "../VenueCard/cardStyles";

// Copies, or shows the link in a prompt when the browser won't (no clipboard, permission denied).
async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success("Link copied.");
  } catch {
    window.prompt("Copy this link:", text);
  }
}

// Edit Venue: make a link for the venue's team. Whoever opens it signs in or creates a ZONA account,
// accepts the Business Terms and becomes an owner of this venue. The link is shown once; make a new one if lost.
export default function VenueInviteLink({ venueId, venueName }) {
  const [invites, setInvites] = useState([]);
  const [loadFailed, setLoadFailed] = useState(false);
  const [made, setMade] = useState(null); // { id, url, venueId } of the link just made
  const [busy, setBusy] = useState(false);
  const [turningOff, setTurningOff] = useState(null);
  const current = useRef(venueId);
  const loadSeq = useRef(0);

  // Only the newest load lands: a slow answer (or one for a venue we left) never overwrites a newer list.
  const load = useCallback(() => {
    const seq = ++loadSeq.current;
    api.get("/venue-invites", { params: { venue_id: venueId } })
      .then(({ data }) => {
        if (seq !== loadSeq.current) return;
        setInvites(data || []);
        setLoadFailed(false);
      })
      .catch(() => { if (seq === loadSeq.current) setLoadFailed(true); });
  }, [venueId]);

  // Another venue's page: nothing from the last venue stays on screen.
  useEffect(() => {
    current.current = venueId;
    setMade(null);
    setBusy(false);
    setInvites([]);
    setLoadFailed(false);
    load();
  }, [venueId, load]);

  const make = async () => {
    const forVenue = venueId;
    setBusy(true);
    try {
      const { data } = await api.post("/venue-invites", { venue_id: forVenue });
      if (current.current !== forVenue) return; // we moved to another venue meanwhile
      setMade({ id: data.id, url: data.url, venueId: forVenue, maxUses: data.max_uses, expiresAt: data.expires_at });
      load();
    } catch (err) {
      if (current.current === forVenue) toast.error(err?.response?.data?.message || "Could not make the link.");
    } finally {
      if (current.current === forVenue) setBusy(false);
    }
  };

  const turnOff = async (id) => {
    if (turningOff === id) return;
    if (!window.confirm("Turn this link off? People who already joined keep their access.")) return;
    setTurningOff(id);
    try {
      await api.delete(`/venue-invites/${id}`);
      if (made?.id === id) setMade(null); // never leave a dead link up to copy
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not turn the link off.");
      load(); // it may already be off (another admin); show the real state
    } finally {
      setTurningOff(null);
    }
  };

  const day = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const live = (i) => !i.revoked_at && new Date(i.expires_at) > new Date();
  const shown = made && made.venueId === venueId ? made : null;

  return (
    <div style={styles.card}>
      <div style={styles.title}>Team invite link</div>
      <p style={styles.help}>
        Send this to {venueName || "the venue"}'s owner or manager. They sign in with their ZONA account or create
        one, accept the Business Terms and land in the business portal for this venue. No app needed. It doesn't
        include payouts (withdrawal requests). If a link is lost or sent to the wrong person, turn it off below.
      </p>
      <Button primaryBtn onClick={make} disabled={busy}>{busy ? "Making…" : "Make a new link"}</Button>

      {shown && (
        <>
          <div style={{ ...styles.row, marginTop: 14, alignItems: "center" }}>
            <input readOnly value={shown.url} onFocus={(e) => e.target.select()} style={{ ...styles.input, flex: "1 1 260px" }} />
            <Button onClick={() => copyText(shown.url)}>Copy</Button>
          </div>
          <div style={{ ...styles.fieldLabel, marginTop: 6 }}>
            For {venueName || "this venue"}: up to {shown.maxUses} people, open until {day(shown.expiresAt)}.
          </div>
        </>
      )}

      {loadFailed && (
        <p style={{ ...styles.help, color: "#ff8a8a", marginTop: 14 }}>
          Couldn't load this venue's links. Older links may still be open; reload before making another.
        </p>
      )}

      {invites.length > 0 && (
        <div style={{ marginTop: 16 }}>
          {invites.map((i) => (
            <div key={i.id} style={styles.inviteRow}>
              <div>
                <div style={{ color: "#fff", fontSize: 14 }}>
                  Made {day(i.created_at)}, {live(i) ? `open until ${day(i.expires_at)}` : i.revoked_at ? "turned off" : "expired"}
                </div>
                <div style={styles.fieldLabel}>
                  {i.uses.length ? `Used by: ${i.uses.map((u) => u.email || "no email").join(", ")}` : "Nobody has used it yet"}
                  {` (${i.uses.length} of ${i.max_uses})`}
                </div>
              </div>
              {live(i) && (
                <button onClick={() => turnOff(i.id)} disabled={turningOff === i.id} style={styles.linkBtn}>
                  {turningOff === i.id ? "Turning off…" : "Turn off"}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  ...cardStyles,
  title: { fontSize: 15, fontWeight: 600, color: "#fff", marginBottom: 8 },
  inviteRow: {
    display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
    padding: "10px 0", borderTop: "1px solid #1a1a1a",
  },
  linkBtn: { background: "none", border: 0, color: "#ff8a8a", fontSize: 13, cursor: "pointer", padding: 0 },
};
